import { Link } from 'react-router-dom'
import { useETSFComplianceStatus } from '../../hooks/useETSFComplianceStatus'
import { useETSFIncidents } from '../../hooks/useETSFIncidents'

export default function Customs() {
  // Z63: the main Customs page is a clean launcher now -- daily checklist / weekly audit /
  // monthly audit / incidents status banners moved to their own sub-module pages (ETSF
  // Compliance already renders the full ETSFComplianceStatusCard at its own top). All this
  // page needs is enough signal to badge the ETSF Compliance card when something's overdue.
  const { status: etsfStatus } = useETSFComplianceStatus()
  const { getStats } = useETSFIncidents()
  const incidentStats = getStats()
  const etsfNeedsAttention = !!etsfStatus && (
    !etsfStatus.dailyChecklist.bhx.completed ||
    !etsfStatus.dailyChecklist.lhr.completed ||
    etsfStatus.weeklyAudit.daysSince === null ||
    etsfStatus.weeklyAudit.daysSince > 10 ||
    (etsfStatus.monthlyAudit.samplesCount < 10 && etsfStatus.monthlyAudit.daysRemaining <= 7) ||
    incidentStats.openCount > 0
  )

  const modules = [
    {
      title: 'POA Management',
      description: 'Manage Power of Attorney documents for importers. Track active, expiring, and expired authorisations.',
      href: '/customs/poa',
      color: 'bg-blue-600 hover:bg-blue-700',
      iconBg: 'bg-blue-100 dark:bg-blue-900',
      iconColor: 'text-blue-600 dark:text-blue-400',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      title: 'Commercial Invoice Management',
      description: 'Manage commercial invoices for customs declarations and shipment documentation.',
      href: '/customs/invoices',
      color: 'bg-green-600 hover:bg-green-700',
      iconBg: 'bg-green-100 dark:bg-green-900',
      iconColor: 'text-green-600 dark:text-green-400',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
    },
    {
      title: 'HS Code Search',
      description: 'Search and validate HS codes against the HMRC tariff database with duty rates and additional codes.',
      href: '/customs/invoices/hs-search',
      color: 'bg-amber-600 hover:bg-amber-700',
      iconBg: 'bg-amber-100 dark:bg-amber-900',
      iconColor: 'text-amber-600 dark:text-amber-400',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
    },
    {
      title: 'HS Code Inspection Log',
      description: 'Record HMRC/Border Force inspections and track inspection results by code.',
      href: '/customs/hs-inspections',
      color: 'bg-indigo-600 hover:bg-indigo-700',
      iconBg: 'bg-indigo-100 dark:bg-indigo-900',
      iconColor: 'text-indigo-600 dark:text-indigo-400',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
    },
    {
      title: 'Clearance Error Log',
      description: 'Record and track clearance mistakes with severity levels and corrective actions.',
      href: '/customs/clearance-errors',
      color: 'bg-rose-600 hover:bg-rose-700',
      iconBg: 'bg-rose-100 dark:bg-rose-900',
      iconColor: 'text-rose-600 dark:text-rose-400',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Monthly Audit Sample',
      description: 'Monthly random sample audits of clearance declarations with pass rate tracking.',
      href: '/customs/monthly-audit',
      color: 'bg-cyan-600 hover:bg-cyan-700',
      iconBg: 'bg-cyan-100 dark:bg-cyan-900',
      iconColor: 'text-cyan-600 dark:text-cyan-400',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      title: 'Customs Letters',
      description: 'Manage customs correspondence, queries, and official communications with HMRC.',
      href: '/customs/letters',
      color: 'bg-purple-600 hover:bg-purple-700',
      iconBg: 'bg-purple-100 dark:bg-purple-900',
      iconColor: 'text-purple-600 dark:text-purple-400',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      title: 'ETSF Compliance',
      description: 'Track HMRC inspections, facility checks and self-audits for BHX and LHR ETSF locations.',
      href: '/customs/etsf',
      color: 'bg-teal-600 hover:bg-teal-700',
      iconBg: 'bg-teal-100 dark:bg-teal-900',
      iconColor: 'text-teal-600 dark:text-teal-400',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Three-Way Verification',
      description: 'Cross-check a Job against its linked Commercial Invoices across shipper, importer, incoterm, commodities, totals and HS codes.',
      href: '/customs/verify',
      color: 'bg-emerald-600 hover:bg-emerald-700',
      iconBg: 'bg-emerald-100 dark:bg-emerald-900',
      iconColor: 'text-emerald-600 dark:text-emerald-400',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
    {
      title: 'HS Code Rules',
      description: 'Customer-instructed HS code replacement rules — auto-suggested during invoice review to save manual corrections.',
      href: '/customs/hs-rules',
      color: 'bg-amber-600 hover:bg-amber-700',
      iconBg: 'bg-amber-100 dark:bg-amber-900',
      iconColor: 'text-amber-600 dark:text-amber-400',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      ),
    },
  ]

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Customs Management</h1>
          <p className="text-gray-600 dark:text-gray-300">
            Manage all customs-related documents and communications.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {modules.map(mod => (
            <div
              key={mod.href}
              className="relative bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
            >
              {mod.href === '/customs/etsf' && etsfNeedsAttention && (
                <span
                  title="Daily checklist, weekly/monthly audit, or incidents need attention"
                  className="absolute top-3 right-3 w-2.5 h-2.5 bg-red-600 rounded-full"
                />
              )}
              <div className="flex items-center mb-4">
                <div className={`p-2 ${mod.iconBg} rounded-lg`}>
                  <span className={mod.iconColor}>{mod.icon}</span>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white ml-3">
                  {mod.title}
                </h2>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm">
                {mod.description}
              </p>
              <Link
                to={mod.href}
                className={`inline-flex items-center px-4 py-2 ${mod.color} text-white rounded-md transition-colors text-sm`}
              >
                Open
                <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
